import {
  CircleAlert,
  Inbox,
  FileCheck2,
  MailPlus,
  MessageSquareQuote,
  UserRoundCheck,
} from "lucide-react";
import type { Notification, NotificationType } from "@facade/contracts";

import { Badge } from "@facade/components/ui";
import type { NotificationItemProps } from "./NotificationItem.types";
import { cn } from "@facade/lib/utils";
import { useMarkNotificationReadMutation } from "../../mutations";
import { useNavigate } from "react-router-dom";

const renderers: Record<
  NotificationType,
  { icon: typeof CircleAlert; className: string }
> = {
  ISSUE_ASSIGNED: {
    icon: UserRoundCheck,
    className: "bg-blue-500/10 text-blue-500",
  },
  ISSUE_MESSAGE_MENTION: {
    icon: MessageSquareQuote,
    className: "bg-cyan-500/10 text-cyan-500",
  },
  ORGANIZATION_INVITATION: {
    icon: MailPlus,
    className: "bg-amber-500/10 text-amber-500",
  },
  PROJECT_INVITATION: {
    icon: Inbox,
    className: "bg-violet-500/10 text-violet-500",
  },
  JOB_SUCCEEDED: {
    icon: FileCheck2,
    className: "bg-emerald-500/10 text-emerald-600",
  },
};

const fallbackRenderer = {
  icon: CircleAlert,
  className: "bg-muted text-muted-foreground",
};

function getNotificationActionUrl(notification: Notification) {
  if (!notification.actionUrl) return;

  const messageId = notification.data.message_id;
  if (
    notification.type !== "ISSUE_MESSAGE_MENTION" ||
    typeof messageId !== "string"
  ) {
    return notification.actionUrl;
  }

  const url = new URL(notification.actionUrl, window.location.origin);
  url.searchParams.set("chatMessageId", messageId);
  return `${url.pathname}${url.search}${url.hash}`;
}

export function NotificationItem({
  notification,
  projectName,
  onSelect,
  className,
}: NotificationItemProps) {
  const navigate = useNavigate();
  const markRead = useMarkNotificationReadMutation();
  const renderer = renderers[notification.type] ?? fallbackRenderer;
  const Icon = renderer.icon;

  const handleSelect = () => {
    if (!notification.readAt) markRead.mutate(notification.id);
    onSelect?.(notification);

    const actionUrl = getNotificationActionUrl(notification);
    if (actionUrl) navigate(actionUrl);
  };

  return (
    <button
      type="button"
      onClick={handleSelect}
      className={cn(
        "flex w-full gap-3 border-b px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-muted/50",
        !notification.readAt && "bg-primary/5",
        notification.readAt && "opacity-60",
        className,
      )}
    >
      <span
        className={cn(
          "mt-0.5 grid size-8 shrink-0 place-items-center rounded-full",
          renderer.className,
        )}
      >
        <Icon className="size-4" />
      </span>

      <span className="min-w-0 flex-1">
        <span className="flex items-start gap-2">
          <span className="flex-1 text-sm font-medium leading-tight">
            {notification.title}
          </span>
          {!notification.readAt ? (
            <span className="mt-1 size-2 shrink-0 rounded-full bg-primary" />
          ) : null}
        </span>

        <span className="mt-1 block text-xs text-muted-foreground">
          {notification.body}
        </span>

        <span className="mt-1.5 flex min-w-0 items-center gap-2">
          {projectName ? (
            <Badge variant="secondary" className="min-w-0 max-w-full shrink">
              <span className="truncate">{projectName}</span>
            </Badge>
          ) : null}
          <span className="shrink-0 text-[0.625rem] text-muted-foreground">
            {new Date(notification.createdAt).toLocaleString()}
          </span>
        </span>
      </span>
    </button>
  );
}
